import {type FC, type MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation, useSearch} from 'wouter';

import type {
  NzbCheckMovie,
  NzbCheckResult,
  NzbHealthDebugData,
  RecentNzbChecksData,
} from '@repo/shared/api/website_api';
import {formatNzbsuCategory} from '@repo/shared/lib/nzbsu_category';
import {errorAsString} from '@repo/shared/lib/type_utils';

import {Button, UnthemedNavLink} from '@repo/shared-web/components/core/button';
import {Input} from '@repo/shared-web/components/core/input';
import {useStringQueryString} from '@repo/shared-web/lib/use_query_string';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatReleaseDate} from '@repo/website-frontend/lib/date';

const PAGE_SIZE = 50;

type TimingKey = keyof Pick<
  NzbCheckResult,
  | 'releaseLookupDurationMs'
  | 'tmdbLookupDurationMs'
  | 'downloadDurationMs'
  | 'parseDurationMs'
  | 'sampleDurationMs'
  | 'nntpConnectDurationMs'
  | 'nntpGroupDurationMs'
  | 'nntpHeaderDurationMs'
  | 'nntpQuitDurationMs'
  | 'probeDurationMs'
>;

const TIMING_COLUMNS: {key: TimingKey; label: string}[] = [
  {key: 'releaseLookupDurationMs', label: 'NZBSU lookup'},
  {key: 'tmdbLookupDurationMs', label: 'TMDB lookup'},
  {key: 'downloadDurationMs', label: 'Download'},
  {key: 'parseDurationMs', label: 'Parse'},
  {key: 'sampleDurationMs', label: 'Sample'},
  {key: 'nntpConnectDurationMs', label: 'NNTP connect'},
  {key: 'nntpGroupDurationMs', label: 'NNTP GROUP'},
  {key: 'nntpHeaderDurationMs', label: 'NNTP HEAD'},
  {key: 'nntpQuitDurationMs', label: 'NNTP QUIT'},
  {key: 'probeDurationMs', label: 'Probe total'},
];

export const NzbHealthDebugPage: FC = () => {
  const [requestedGuid, setRequestedGuid, requestedGuidRef] = useStringQueryString('guid', '');
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const [searchGuid, setSearchGuid] = useState(requestedGuid);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recentChecks, setRecentChecks] = useState<RecentNzbChecksData | undefined>();
  const [releaseDetails, setReleaseDetails] = useState<NzbHealthDebugData | undefined>();
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingRelease, setLoadingRelease] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showDetailedTimes, setShowDetailedTimes] = useState(false);
  const [movieInfoByNzbId, setMovieInfoByNzbId] = useState<Record<string, NzbCheckMovie>>({});

  useEffect(() => {
    setSearchGuid(requestedGuid);
  }, [requestedGuid]);

  useEffect(() => {
    let active = true;
    setLoadingRecent(true);
    setError(undefined);
    apiCall('POST /list-recent-nzb-checks', {paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setRecentChecks(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRecent(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  useEffect(() => {
    const guid = requestedGuid.trim();
    if (!guid) {
      setReleaseDetails(undefined);
      setLoadingRelease(false);
      return;
    }

    let active = true;
    setLoadingRelease(true);
    setReleaseDetails(undefined);
    setError(undefined);
    apiCall('POST /get-nzb-health-debug', {guid, paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setReleaseDetails(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRelease(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey, requestedGuid]);

  const displayedNzbIds = useMemo(() => {
    const checks = requestedGuid
      ? releaseDetails?.guid === requestedGuid
        ? releaseDetails.checks
        : undefined
      : recentChecks?.items;
    return [...new Set(checks?.map(check => check.nzbId) ?? [])];
  }, [recentChecks?.items, releaseDetails?.checks, requestedGuid]);

  useEffect(() => {
    if (displayedNzbIds.length === 0) {
      return;
    }

    let active = true;
    apiCall('POST /get-nzb-check-movies', {nzbIds: displayedNzbIds})
      .then(({items}) => {
        if (active) {
          setMovieInfoByNzbId(current => ({
            ...current,
            ...Object.fromEntries(items.map(item => [item.nzbId, item])),
          }));
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      });
    return () => {
      active = false;
    };
  }, [displayedNzbIds, refreshKey]);

  const handleRefreshClick = useCallback(() => {
    setRefreshKey(current => current + 1);
  }, []);

  const handleNavigateToRelease = useCallback(
    (guid: string) => {
      const query = new URLSearchParams(search);
      query.set('guid', guid);
      setLocation(`${location}?${query.toString()}`);
    },
    [location, search, setLocation],
  );

  const handleInspectClick = useCallback(() => {
    handleNavigateToRelease(searchGuid.trim());
  }, [handleNavigateToRelease, searchGuid]);

  const handleInspectRowClick = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      const guid = evt.currentTarget.getAttribute('data-guid');
      if (!guid) {
        return;
      }
      setSearchGuid(guid);
      handleNavigateToRelease(guid);
    },
    [handleNavigateToRelease],
  );

  const handleCloseReleaseClick = useCallback(() => {
    setRequestedGuid('');
    setReleaseDetails(undefined);
  }, [setRequestedGuid]);

  const handleLoadMoreClick = useCallback(async () => {
    if (!recentChecks?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /list-recent-nzb-checks', {
        limit: PAGE_SIZE,
        paginationToken: recentChecks.paginationToken,
      });
      setRecentChecks(current =>
        current
          ? {
              items: [...current.items, ...nextPage.items],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      setError(errorAsString(err));
    } finally {
      setLoadingMore(false);
    }
  }, [recentChecks?.paginationToken]);

  const handleLoadOlderReleaseChecksClick = useCallback(async () => {
    if (!requestedGuid || !releaseDetails?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /get-nzb-health-debug', {
        guid: requestedGuid,
        limit: PAGE_SIZE,
        paginationToken: releaseDetails.paginationToken,
      });
      setReleaseDetails(current =>
        requestedGuidRef.current === requestedGuid && current?.guid === requestedGuid
          ? {
              ...current,
              checks: [...current.checks, ...nextPage.checks],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      if (requestedGuidRef.current === requestedGuid) {
        setError(errorAsString(err));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [releaseDetails?.paginationToken, requestedGuid, requestedGuidRef]);

  const handleRunCheckClick = useCallback(async () => {
    await apiCall('POST /check-nzb', {id: requestedGuid});
    setRefreshKey(current => current + 1);
  }, [requestedGuid]);

  return (
    <Wrapper>
      <PageHeader>
        <h1>NZB checks</h1>
        <ActionGroup>
          <ShowDetailedTimes>
            <input
              type="checkbox"
              checked={showDetailedTimes}
              onChange={evt => setShowDetailedTimes(evt.target.checked)}
            />
            Show detailed times
          </ShowDetailedTimes>
          {requestedGuid ? (
            <Button onClick={handleCloseReleaseClick}>Back to checks</Button>
          ) : (
            <Button onClick={handleRefreshClick} disabled={loadingRecent}>
              Refresh
            </Button>
          )}
        </ActionGroup>
      </PageHeader>

      {error ? <ErrorMessage>{error}</ErrorMessage> : <></>}

      {requestedGuid ? (
        <Panel>
          {loadingRelease ? (
            <Message>Loading release details…</Message>
          ) : releaseDetails ? (
            <>
              <SectionHeader>
                <div>
                  <h2>{releaseDetails.release?.title ?? 'Release details'}</h2>
                  <Guid>{releaseDetails.guid}</Guid>
                  {releaseDetails.release ? (
                    <ReleaseDate>
                      Posted {formatReleaseDate(releaseDetails.release.pubTs)}
                    </ReleaseDate>
                  ) : (
                    <></>
                  )}
                </div>
                <ActionGroup>
                  <Button onClickAsync={handleRunCheckClick}>Check now</Button>
                </ActionGroup>
              </SectionHeader>

              <PreviousChecksHeading>Check history</PreviousChecksHeading>
              {releaseDetails.checks.length ? (
                <TableScroll>
                  <ChecksTable>
                    <thead>
                      <tr>
                        <th>Checked</th>
                        <th>Movie</th>
                        <th>NZBSU category</th>
                        <th>Result</th>
                        <th>Found</th>
                        <th>Missing</th>
                        <th>Sampled</th>
                        <th>Time</th>
                        {showDetailedTimes
                          ? TIMING_COLUMNS.map(({key, label}) => <th key={key}>{label}</th>)
                          : null}
                      </tr>
                    </thead>
                    <tbody>
                      {releaseDetails.checks.map(check => (
                        <tr key={check.id}>
                          <td>{formatTimestamp(check.ts)}</td>
                          <td>
                            <CheckedMovie movie={movieInfoByNzbId[check.nzbId]} />
                          </td>
                          <td>
                            <CategoryValue title={formatNzbsuCategory(check.nzbsuCategory)}>
                              {formatNzbsuCategory(check.nzbsuCategory) ?? '—'}
                            </CategoryValue>
                          </td>
                          <td>
                            {check.outcome === 'failed'
                              ? 'Check failed'
                              : check.checked === 0
                                ? 'Nothing tested'
                                : check.failure === 0
                                  ? 'Sample passed'
                                  : 'Missing parts'}
                          </td>
                          <td>{check.success}</td>
                          <td>{check.failure}</td>
                          <td>{`${check.checked} of ${check.total}`}</td>
                          <td>
                            <TotalDuration>{formatDuration(check.totalDurationMs)}</TotalDuration>
                          </td>
                          {showDetailedTimes
                            ? TIMING_COLUMNS.map(({key}) => (
                                <td key={key}>{formatDuration(check[key])}</td>
                              ))
                            : null}
                        </tr>
                      ))}
                    </tbody>
                  </ChecksTable>
                </TableScroll>
              ) : (
                <Message>No saved checks for this release.</Message>
              )}

              {releaseDetails.paginationToken ? (
                <LoadMore onClickAsync={handleLoadOlderReleaseChecksClick} loading={loadingMore}>
                  Load older checks
                </LoadMore>
              ) : (
                <></>
              )}
            </>
          ) : (
            <Message>
              Release details aren’t available. Go back to the checks list and try again.
            </Message>
          )}
        </Panel>
      ) : (
        <>
          <Panel>
            {loadingRecent ? (
              <Message>Loading recent checks…</Message>
            ) : recentChecks?.items.length ? (
              <TableScroll>
                <ChecksTable>
                  <thead>
                    <tr>
                      <th>Checked</th>
                      <th>Release</th>
                      <th>Movie</th>
                      <th>NZBSU category</th>
                      <th>Result</th>
                      <th>Found</th>
                      <th>Missing</th>
                      <th>Sampled</th>
                      <th>Time</th>
                      {showDetailedTimes
                        ? TIMING_COLUMNS.map(({key, label}) => <th key={key}>{label}</th>)
                        : null}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentChecks.items.map(check => (
                      <tr key={check.id}>
                        <td>{formatTimestamp(check.ts)}</td>
                        <td>
                          <ReleaseTitle>{check.title ?? 'Release title unavailable'}</ReleaseTitle>
                        </td>
                        <td>
                          <CheckedMovie movie={movieInfoByNzbId[check.nzbId]} />
                        </td>
                        <td>
                          <CategoryValue title={formatNzbsuCategory(check.nzbsuCategory)}>
                            {formatNzbsuCategory(check.nzbsuCategory) ?? '—'}
                          </CategoryValue>
                        </td>
                        <td>
                          <ResultBadge
                            $state={
                              check.outcome === 'failed'
                                ? 'failed'
                                : check.checked === 0
                                  ? 'empty'
                                  : check.failure > 0
                                    ? 'missing'
                                    : 'found'
                            }
                          >
                            {check.outcome === 'failed'
                              ? 'Check failed'
                              : check.checked === 0
                                ? 'Nothing tested'
                                : check.failure === 0
                                  ? 'Sample passed'
                                  : 'Missing parts'}
                          </ResultBadge>
                        </td>
                        <td>{check.success}</td>
                        <td>{check.failure}</td>
                        <td>{`${check.checked} of ${check.total}`}</td>
                        <td>{formatDuration(check.totalDurationMs)}</td>
                        {showDetailedTimes
                          ? TIMING_COLUMNS.map(({key}) => (
                              <td key={key}>{formatDuration(check[key])}</td>
                            ))
                          : null}
                        <td>
                          <Button
                            data-guid={check.nzbId}
                            onClick={handleInspectRowClick}
                            overrides={{
                              paddingTop: 4,
                              paddingRight: 10,
                              paddingBottom: 4,
                              paddingLeft: 10,
                              fontSize: 13,
                              lineHeight: 1.2,
                            }}
                          >
                            Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ChecksTable>
              </TableScroll>
            ) : (
              <Message>
                No checks yet. Open a release on NZB.su and choose “Check” to add the first result.
              </Message>
            )}

            {recentChecks?.paginationToken ? (
              <LoadMore onClickAsync={handleLoadMoreClick} loading={loadingMore}>
                Load older checks
              </LoadMore>
            ) : (
              <></>
            )}
          </Panel>

          <Panel>
            <SectionHeader>
              <h2>Find a release</h2>
            </SectionHeader>
            <LookupRow>
              <Input<string>
                aria-label="NZB release ID"
                placeholder="Paste an NZB release ID"
                value={searchGuid}
                syncState={setSearchGuid}
              />
              <Button onClick={handleInspectClick} disabled={searchGuid.trim().length === 0}>
                View release
              </Button>
            </LookupRow>
          </Panel>
        </>
      )}
    </Wrapper>
  );
};

NzbHealthDebugPage.displayName = 'NzbHealthDebugPage';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(durationMs: number | undefined): string {
  if (durationMs === undefined) {
    return '—';
  }
  return durationMs < 1000
    ? `${durationMs} ms`
    : `${(durationMs / 1000).toFixed(durationMs < 10_000 ? 1 : 0)} s`;
}

interface CheckedMovieProps {
  movie?: NzbCheckMovie;
}

const CheckedMovie: FC<CheckedMovieProps> = ({movie}) =>
  movie?.movieTitle && movie.imdbId ? (
    <MovieLink to={`/browse/${movie.imdbId}`} title={movie.movieTitle}>
      {movie.movieTitle}
    </MovieLink>
  ) : (
    <UnavailableMovie>{movie?.movieTitle ?? '—'}</UnavailableMovie>
  );

CheckedMovie.displayName = 'CheckedMovie';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  max-width: 1400px;
  width: 100%;
  align-self: center;
`;

const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  h1 {
    margin: 0;
  }
`;

const Panel = styled.section`
  background: #1f242f;
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  padding: 16px;
  h2 {
    margin: 0 0 6px;
    font-size: 18px;
  }
  p {
    margin: 0;
    color: #ffffffbb;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const LookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  > input {
    flex: 1;
    min-width: 0;
  }
  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShowDetailedTimes = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #ffffffbb;
  font-size: 13px;
  white-space: nowrap;
  input {
    margin: 0;
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const ChecksTable = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  table-layout: auto;
  width: max-content;
  text-align: left;
  th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
  }
  th,
  td {
    border-bottom: 1px solid #ffffff12;
    padding: 11px 10px;
    white-space: nowrap;
  }
  td {
    padding-top: 0;
    padding-bottom: 0;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
    text-align: right;
  }
`;

const ReleaseTitle = styled.div`
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MovieLink = styled(UnthemedNavLink)`
  display: inline-block;
  max-width: 300px;
  color: #a9d2ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UnavailableMovie = styled.span`
  color: #ffffff77;
`;

const CategoryValue = styled.div`
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TotalDuration = styled.div`
  white-space: nowrap;
`;

const Guid = styled.div`
  color: #ffffff77;
  font-family: monospace;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

const ResultBadge = styled.span<{$state: 'empty' | 'missing' | 'found' | 'failed'}>`
  color: ${p =>
    p.$state === 'failed'
      ? '#ff8585'
      : p.$state === 'missing'
        ? '#ffcb8a'
        : p.$state === 'found'
          ? '#9ee6ae'
          : '#ffffff88'};
  white-space: nowrap;
`;

const ReleaseDate = styled.p`
  margin-top: 6px !important;
`;

const PreviousChecksHeading = styled.h3`
  font-size: 15px;
  margin: 0 0 4px;
`;

const LoadMore = styled(Button)`
  margin-top: 12px;
`;

const Message = styled.div`
  color: #ffffff88;
  padding: 8px 0;
`;

const ErrorMessage = styled(Message)`
  color: #ff8585;
`;
